import { useEffect, useRef, useState } from "react";
import { useForm } from "@tanstack/react-form";
import { isValidEmail } from "@sellar-io/common";
import { Button, Icon, Switch } from "@sellar-io/components";
import {
  Dialog as AccessibleDialog,
  Switch as AccessibleSwitch,
} from "@headlessui/react";
import { Dialog } from "../../../components/modal/Dialog";
import { useRemoteFeatureFlags } from "../../../hooks/useRemoteFeatureFlags";
import type { CheckoutInvoiceMethod } from "../../stockList/checkoutV2/lib/types";

type Settings = {
  invoiceMethod: CheckoutInvoiceMethod;
  paymentLinkRequested: boolean;
};
type Props = {
  isOpen: boolean;
  onDismiss: () => void;
  supplierName: string;
  stripeAccountId?: string | null;
  orderTotal: number;
  initialSettings: Settings;
  onConfirm: (settings: Settings) => void;
  invoiceEmails: string[];
  onUpdateInvoiceEmails: (emails: string[]) => Promise<void>;
};

export const UnifiedCheckoutPaymentLinkSettingsDialog = ({
  isOpen,
  onDismiss,
  supplierName,
  stripeAccountId,
  orderTotal,
  initialSettings,
  onConfirm,
  invoiceEmails,
  onUpdateInvoiceEmails,
}: Props) => {
  const { isActive } = useRemoteFeatureFlags();
  const usePaymentLinks = isActive("checkoutPaymentLinkRequest");
  const [settings, setSettings] = useState(initialSettings);
  const [editing, setEditing] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saving, setSaving] = useState(false);
  const emailInput = useRef<HTMLInputElement>(null);
  const wasOpen = useRef(false);
  useEffect(() => {
    if (editing) emailInput.current?.focus();
  }, [editing]);
  const form = useForm({
    defaultValues: { emails: invoiceEmails.join(", ") },
    onSubmit: async ({ value }) => {
      setSaving(true);
      setSaveError("");
      try {
        await onUpdateInvoiceEmails(
          value.emails.split(",").map((email) => email.trim()),
        );
        setEditing(false);
      } catch {
        setSaveError(
          "Unable to save invoice email addresses. Please try again.",
        );
      } finally {
        setSaving(false);
      }
    },
  });
  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      setSettings(initialSettings);
      setEditing(false);
      setSaveError("");
      form.reset({ emails: invoiceEmails.join(", ") });
    }
    wasOpen.current = isOpen;
  }, [isOpen, initialSettings, invoiceEmails, form]);
  const dismiss = () => {
    if (!saving) onDismiss();
  };
  return (
    <Dialog
      active={isOpen}
      onDismiss={dismiss}
      className="m-4 h-fit max-h-[calc(100dvh-2rem)]"
    >
      <div className="flex flex-col gap-8 p-4">
        <div className="relative text-center">
          <button
            type="button"
            aria-label="Close payment link settings"
            disabled={saving}
            onClick={dismiss}
            className="absolute top-0 left-0"
          >
            <Icon icon="CHEVRON_DOWN" />
          </button>
          <AccessibleDialog.Title className="text-lg leading-6 font-semibold">
            Payment link settings
          </AccessibleDialog.Title>
          <AccessibleDialog.Description className="mt-2 text-zinc-600">
            Below are the payment methods available to you with {supplierName}
          </AccessibleDialog.Description>
        </div>
        <div className="flex flex-col gap-4">
          {usePaymentLinks ? (
            Boolean(stripeAccountId) &&
            orderTotal > 0 && (
              <AccessibleSwitch.Group
                as="div"
                className="flex items-center gap-4 rounded-lg bg-zinc-100 p-4"
              >
                <Switch
                  checked={settings.paymentLinkRequested}
                  onChange={(paymentLinkRequested) =>
                    setSettings({
                      paymentLinkRequested,
                      invoiceMethod: "manual-invoice",
                    })
                  }
                />
                <div>
                  <AccessibleSwitch.Label as="p" className="font-semibold">
                    Send a card payment link
                  </AccessibleSwitch.Label>
                  <p className="text-zinc-600">
                    A link to pay by card will be sent to the email address(es)
                    below when payment for this order is due. You can always
                    request one later.
                  </p>
                </div>
              </AccessibleSwitch.Group>
            )
          ) : (
            <fieldset className="flex flex-col gap-2">
              <legend className="mb-2 font-semibold">Invoice method</legend>
              {(
                [
                  ["stripe-invoice", "Card payment link"],
                  ["manual-invoice", "BACS manual invoice"],
                ] as const
              ).map(([value, label]) => (
                <label
                  key={value}
                  className="flex items-center gap-2 rounded-lg border p-3"
                >
                  <input
                    type="radio"
                    name="unified-invoice-method"
                    checked={settings.invoiceMethod === value}
                    onChange={() =>
                      setSettings({ ...settings, invoiceMethod: value })
                    }
                  />
                  {label}
                </label>
              ))}
            </fieldset>
          )}
          <form
            onSubmit={(event) => {
              event.preventDefault();
              event.stopPropagation();
              if (editing && !saving) void form.handleSubmit();
            }}
            className="flex flex-col gap-1"
          >
            <form.Field
              name="emails"
              validators={{
                onSubmit: ({ value }) =>
                  value.trim() &&
                  value.split(",").every((email) => isValidEmail(email.trim()))
                    ? undefined
                    : "Only provide valid emails, separated by commas",
              }}
            >
              {(field) => (
                <>
                  <label
                    htmlFor="unified-invoice-emails"
                    className="font-semibold"
                  >
                    Invoice email address
                  </label>
                  <div className="flex overflow-hidden rounded-lg border border-zinc-300">
                    <input
                      ref={emailInput}
                      id="unified-invoice-emails"
                      name={field.name}
                      type="text"
                      required
                      readOnly={!editing}
                      disabled={saving}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(event) => {
                        field.handleChange(event.target.value);
                        setSaveError("");
                      }}
                      aria-invalid={field.state.meta.errors.length > 0}
                      aria-describedby="unified-invoice-email-feedback"
                      className="min-w-0 flex-1 bg-white px-3 py-2 focus:outline-purple-500"
                    />
                    {editing ? (
                      <Button
                        type="submit"
                        loading={saving}
                        disabled={saving}
                        className="rounded-none"
                      >
                        Save
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        onClick={() => setEditing(true)}
                        className="rounded-none bg-zinc-100 text-zinc-800 hover:bg-zinc-200"
                      >
                        <Icon icon="PENCIL_OUTLINE" size={0.6} />
                        Edit
                      </Button>
                    )}
                  </div>
                  <div id="unified-invoice-email-feedback" aria-live="polite">
                    {editing && (
                      <p className="text-sm text-zinc-500">
                        Updating your invoice email address(es) here will update
                        them across your business.
                      </p>
                    )}
                    {field.state.meta.errors.map((error) => (
                      <p key={error} className="text-sm text-red-700">
                        {error}
                      </p>
                    ))}
                    {saveError && (
                      <p role="alert" className="text-sm text-red-700">
                        {saveError}
                      </p>
                    )}
                  </div>
                </>
              )}
            </form.Field>
          </form>
        </div>
        <div className="flex justify-end">
          <Button
            disabled={saving}
            onClick={() => {
              onConfirm(settings);
              onDismiss();
            }}
          >
            Confirm
          </Button>
        </div>
      </div>
    </Dialog>
  );
};
